// src/api/News.js
import { http, resolveWpAssetUrl } from "../lib/http";
import { getLangFromCookie } from "../Utils/LangCookie";

/* ----------------------------------------------------------
   Helpers
---------------------------------------------------------- */

function getLangParam() {
  try {
    const v = getLangFromCookie?.();
    const s = String(v || "").toUpperCase();
    return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
  } catch {
    return "ZH";
  }
}

function withLang(params = {}) {
  return { ...params, lang: getLangParam() };
}

/**
 * IMPORTANT:
 * - News content in WP may contain relative <img src="/uploads/..."> or "/wp-content/..."
 * - We must rewrite src to WP absolute URLs
 */
function rewriteHtmlAssetSrc(html) {
  if (!html) return html;
  const s = String(html);

  return s.replace(/\bsrc=(["'])([^"']+)\1/gi, (full, q, src) => {
    return `src=${q}${resolveWpAssetUrl(src)}${q}`;
  });
}

function normalizeNewsRow(row) {
  if (!row) return row;
  const n = typeof row.toJSON === "function" ? row.toJSON() : { ...row };

  // thumbnail
  if (n.thumbnail) {
    if (n.thumbnail.url) n.thumbnail.url = resolveWpAssetUrl(n.thumbnail.url);
    if (n.thumbnail.asset?.url)
      n.thumbnail.asset.url = resolveWpAssetUrl(n.thumbnail.asset.url);
  }

  // assets array
  if (Array.isArray(n.assets)) {
    n.assets = n.assets.map((a) => ({
      ...a,
      url: resolveWpAssetUrl(a?.url),
    }));
  }

  // content HTML src rewrite
  if (n.content) n.content = rewriteHtmlAssetSrc(n.content);
  if (n.content_html) n.content_html = rewriteHtmlAssetSrc(n.content_html);

  return n;
}

function normalizeGroupedResponse(res) {
  if (!res || !res.grouped) return res;
  const grouped = {};
  for (const [k, arr] of Object.entries(res.grouped)) {
    grouped[k] = Array.isArray(arr) ? arr.map(normalizeNewsRow) : [];
  }
  return { ...res, grouped };
}

function buildAssetFormData(file) {
  const fd = new FormData();
  fd.append("file", file);
  return fd;
}

function buildThumbnailFormData(file, alt) {
  const fd = new FormData();
  fd.append("thumbnail", file);
  if (alt != null && String(alt).trim() !== "") fd.append("alt", String(alt).trim());
  return fd;
}

function buildNewsFormData(payload = {}) {
  const fd = new FormData();

  if (payload.title !== undefined) fd.append("title", payload.title);
  if (payload.title_en !== undefined) fd.append("title_en", payload.title_en);
  if (payload.title_jp !== undefined) fd.append("title_jp", payload.title_jp);

  if (payload.title_EN !== undefined && payload.title_en === undefined)
    fd.append("title_en", payload.title_EN);
  if (payload.title_JP !== undefined && payload.title_jp === undefined)
    fd.append("title_jp", payload.title_JP);

  if (payload.content !== undefined) fd.append("content", payload.content);

  const postDate = payload.postDate || payload.date;
  if (postDate !== undefined && postDate !== null && String(postDate).trim() !== "") {
    fd.append("postDate", postDate);
  }

  if (payload.startDate !== undefined && payload.startDate !== null && String(payload.startDate).trim() !== "") {
    fd.append("startDate", payload.startDate);
  }

  if (payload.endDate !== undefined && payload.endDate !== null && String(payload.endDate).trim() !== "") {
    fd.append("endDate", payload.endDate);
  }

  if (payload.newsType !== undefined && payload.newsType !== null && String(payload.newsType).trim() !== "") {
    fd.append("newsType", String(payload.newsType).trim());
  } else if (payload.type !== undefined && payload.type !== null && String(payload.type).trim() !== "") {
    fd.append("type", String(payload.type).trim());
  }

  if (payload.status !== undefined && payload.status !== null && String(payload.status).trim() !== "") {
    fd.append("status", payload.status);
  }

  if (payload.slug !== undefined && payload.slug !== null && String(payload.slug).trim() !== "") {
    fd.append("slug", payload.slug);
  }

  // tags (repeatable)
  if (Array.isArray(payload.tags)) {
    payload.tags
      .map((t) => (t != null ? String(t).trim() : "" ))
      .filter(Boolean)
      .forEach((t) => fd.append("tags", t));
  } else if (payload.tags != null) {
    const s = String(payload.tags).trim();
    if (s) fd.append("tags", s);
  }

  // thumbnail in same request (create)
  if (payload.thumbnailFile instanceof File) {
    fd.append("thumbnail", payload.thumbnailFile);

    const alt =
      payload.thumbnailAlt !== undefined
        ? payload.thumbnailAlt
        : payload.alt !== undefined
        ? payload.alt
        : "";

    if (alt != null && String(alt).trim() !== "") {
      fd.append("thumbnailAlt", String(alt).trim());
    }
  } else {
    // legacy style fields
    const thumbObj = payload.thumbnail || null;
    const assetId = thumbObj?.assetId ?? payload.thumbnailAssetId ?? payload.assetId;
    const url = thumbObj?.url ?? payload.thumbnailUrl ?? payload.url;
    const alt = thumbObj?.alt ?? payload.thumbnailAlt ?? payload.alt;

    if (assetId != null && String(assetId).trim() !== "") fd.append("thumbnailAssetId", String(assetId));
    if (url != null && String(url).trim() !== "") fd.append("thumbnailUrl", String(url).trim());
    if (alt != null && String(alt).trim() !== "") fd.append("thumbnailAlt", String(alt).trim());
  }

  return fd;
}

/* ----------------------------------------------------------
   ✅ GET de-duplication (fix React 18 StrictMode double fetch)
   - If the same GET (path+params+lang) is requested twice quickly,
     only the first one hits the network.
---------------------------------------------------------- */

const __getInflight = new Map(); // key -> Promise
const __getRecent = new Map();   // key -> { at:number, value:any }
const __RECENT_TTL_MS = 800;

function __stableStringify(obj) {
  if (obj == null) return "";
  if (typeof obj !== "object") return String(obj);

  if (Array.isArray(obj)) {
    return `[${obj.map(__stableStringify).join(",")}]`;
  }

  const keys = Object.keys(obj).sort();
  const parts = [];
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined) continue;
    parts.push(`${k}:${__stableStringify(v)}`);
  }
  return `{${parts.join("|")}}`;
}

function __getKey(path, config) {
  const params = config?.params || {};
  // include baseURL implicitly by path (we always call getWithFallback for both new+legacy)
  return `GET:${String(path)}:params=${__stableStringify(params)}`;
}

async function __dedupeGet(path, config, runner) {
  const key = __getKey(path, config);

  // if a very recent successful response exists, reuse it (prevents StrictMode “second mount” call)
  const recent = __getRecent.get(key);
  if (recent && Date.now() - recent.at <= __RECENT_TTL_MS) {
    return recent.value;
  }

  // if already in-flight, reuse the same promise
  const inflight = __getInflight.get(key);
  if (inflight) return inflight;

  const p = (async () => {
    try {
      const res = await runner();
      // keep a short-lived “recent success” cache
      __getRecent.set(key, { at: Date.now(), value: res });
      return res;
    } finally {
      __getInflight.delete(key);
    }
  })();

  __getInflight.set(key, p);
  return p;
}

/**
 * Try new namespace first, fallback to legacy if route doesn't exist.
 * - WP REST "no route" typically returns 404 with a WP error payload.
 */
async function getWithFallback(path, config) {
  return __dedupeGet(path, config, async () => {
    return await http.get(path, config);
  });
}

async function postWithFallback(path, data, config) {
  return await http.post(path, data, config);
}

async function putWithFallback(path, data, config) {
  return await http.put(path, data, config);
}

async function delWithFallback(path, config) {
  return await http.delete(path, config);
}

/* ----------------------------------------------------------
   Public APIs
---------------------------------------------------------- */

export async function apiGetNews({ order = "desc", limit, offset, q, status, newsType } = {}) {
  const { data } = await getWithFallback("/news", {
    params: withLang({ order, limit, offset, q, status, newsType }),
  });
  return Array.isArray(data) ? data.map(normalizeNewsRow) : data;
}

export async function apiGetLatestNews(limit = 5, newsType) {
  const { data } = await getWithFallback("/news/latest", {
    params: withLang({ limit, newsType }),
  });
  return Array.isArray(data) ? data.map(normalizeNewsRow) : data;
}

export async function apiGetShowcaseNews({
  limit = 3,
  newsType,
  skipLoading = false,
} = {}) {
  const { data } = await getWithFallback("/news/showcase", {
    params: withLang({ limit, newsType }),
    skipLoading,
  });

  return Array.isArray(data)
    ? data.map(normalizeNewsRow)
    : data;
}

export async function apiGetNewsGrouped({ newsType, limitPerType } = {}) {
  const { data } = await getWithFallback("/news/grouped", {
    params: withLang({ newsType, limitPerType }),
  });
  return normalizeGroupedResponse(data);
}

export async function apiReadNews({ id, slug, newsType } = {}) {
  const params = {};
  if (id !== undefined && id !== null && String(id).trim() !== "") params.id = id;
  if (slug !== undefined && slug !== null && String(slug).trim() !== "") params.slug = String(slug).trim();
  if (newsType !== undefined && newsType !== null && String(newsType).trim() !== "")
    params.newsType = String(newsType).trim();

  if (!params.id && !params.slug) throw new Error("apiReadNews: missing identifier (need id or slug)");

  const { data } = await getWithFallback("/news/read", {
    params: withLang(params),
  });
  return normalizeNewsRow(data);
}

export async function apiGetNewsAllLegacy({ limit = 20, page = 1, type, tagId, status } = {}) {
  const { data } = await getWithFallback("/news/all", {
    params: withLang({ limit, page, type, tagId, status }),
  });

  if (data?.rows && Array.isArray(data.rows)) {
    return { ...data, rows: data.rows.map(normalizeNewsRow) };
  }
  return data;
}

export async function apiGetNewsById(id) {
  const { data } = await getWithFallback(`/news/${id}`, {
    params: withLang({}),
  });
  return normalizeNewsRow(data);
}

/* ----------------------------------------------------------
   CMS APIs
---------------------------------------------------------- */

export async function apiCreateNews(payload) {
  const formData = buildNewsFormData(payload);
  const { data } = await postWithFallback("/news/create", formData, {
    params: withLang({}),
  });
  return normalizeNewsRow(data);
}

export async function apiUpdateNews(id, payload) {
  const formData = buildNewsFormData(payload);
  const { data } = await putWithFallback(`/news/${id}`, formData, {
    params: withLang({}),
  });
  return normalizeNewsRow(data);
}

export async function apiDeleteNews(id) {
  const { data } = await delWithFallback(`/news/${id}`, {
    params: withLang({}),
  });
  return data;
}

/* ---------------- Assets ---------------- */

export async function apiGetNewsAssets(newsId) {
  const { data } = await getWithFallback(`/news/${newsId}/assets`, {
    params: withLang({}),
  });
  return Array.isArray(data)
    ? data.map((a) => ({ ...a, url: resolveWpAssetUrl(a?.url) }))
    : data;
}

export async function apiUploadNewsAsset(newsId, file) {
  const formData = buildAssetFormData(file);
  const { data } = await postWithFallback(`/news/${newsId}/assets`, formData, {
    params: withLang({}),
  });
  return { ...data, url: resolveWpAssetUrl(data?.url) };
}

export async function apiDeleteNewsAsset(newsId, assetId) {
  const { data } = await delWithFallback(`/news/${newsId}/assets/${assetId}`, {
    params: withLang({}),
  });
  return data;
}

/* ---------------- Thumbnail ---------------- */

export async function apiGetNewsThumbnail(newsId) {
  const { data } = await getWithFallback(`/news/${newsId}/thumbnail`, {
    params: withLang({}),
  });
  if (!data) return null;

  const t = typeof data.toJSON === "function" ? data.toJSON() : { ...data };
  if (t.url) t.url = resolveWpAssetUrl(t.url);
  if (t.asset?.url) t.asset.url = resolveWpAssetUrl(t.asset?.url);
  return t;
}

export async function apiUploadNewsThumbnail(newsId, file, alt) {
  const formData = buildThumbnailFormData(file, alt);
  const { data } = await postWithFallback(`/news/${newsId}/thumbnail`, formData, {
    params: withLang({}),
  });

  return {
    ...data,
    url: resolveWpAssetUrl(data?.url),
    asset: data?.asset ? { ...data.asset, url: resolveWpAssetUrl(data.asset?.url) } : data?.asset,
  };
}

export async function apiReplaceNewsThumbnail(newsId, file, alt) {
  const formData = buildThumbnailFormData(file, alt);
  const { data } = await putWithFallback(`/news/${newsId}/thumbnail`, formData, {
    params: withLang({}),
  });

  return {
    ...data,
    url: resolveWpAssetUrl(data?.url),
    asset: data?.asset ? { ...data.asset, url: resolveWpAssetUrl(data.asset?.url) } : data?.asset,
  };
}

export async function apiDeleteNewsThumbnail(newsId) {
  const { data } = await delWithFallback(`/news/${newsId}/thumbnail`, {
    params: withLang({}),
  });
  return data;
}

/* ----------------------------------------------------------
   Debug
---------------------------------------------------------- */

export const NEWS_REST_API_BASE = http.defaults.baseURL;